import { DateTime } from 'luxon';
import { t } from 'i18next';
import { KiloUnit } from './helper';
interface InstanceDetail {
  resource: Record<string, any>;
  disk: Record<string, any>;
  license: Record<string, any>;
}
export const getDeviceDetail = (asset: Record<string, any>[]) => {
  const devices = asset?.filter(
    (a: Record<string, any>) => a.type === 'DEVICE_LEVEL' && a.status === 1
  );

  const health = devices?.reduce(
    (res: Record<string, any>, device: Record<string, any>) => {
      const isActive = device.device_health !== 'inactive';
      if (!isActive) {
        res.inactive_sn.push({ SN: device.sn, status: 'Inactive' });
      } else {
        res.active_sn.push({ SN: device.sn, status: 'Active' });
      }
      return {
        inactive: isActive ? res.inactive : res.inactive + 1,
        active: isActive ? res.active + 1 : res.active,
        inactive_sn: res.inactive_sn,
        active_sn: res.active_sn
      };
    },
    { inactive: 0, active: 0, inactive_sn: [], active_sn: [] }
  );

  return {
    number: devices ? devices.length : '',
    health: devices ? health : {}
  };
};
export const getInstanceDetail = (
  instance: Record<string, any>,
  daysToRemindExpiry?: number,
  evaluateQuotaRatio?: number
): InstanceDetail => {
  let resource: Record<string, any> = {};
  let disk: Record<string, any> = {};
  let license: Record<string, any> = {};
  const formateDecimal = (value: string | number): string => {
    return parseFloat(value + '').toFixed(2);
  };
  resource = {
    size: instance?.entitled_quota_per_day || 0,
    addon: instance?.entitled_shared_quota_per_day || 0,
    used:
      instance?.quota_used_last_24_hours || instance?.quota_used_yesterday || 0,
    total: instance?.quota_used_total || 0,
    speed: parseFloat(instance?.lograte || 0).toFixed(2)
  };
  resource.size_gb = resource.size / KiloUnit;
  resource.addon_gb = resource.addon / KiloUnit;
  resource.entitledSize = resource.size; // entitle_quota_per_day already included shared_quota_per_day
  resource.percent =
    resource?.used && resource?.entitledSize > 0
      ? (resource?.used * 100) / resource?.entitledSize
      : 0;
  disk = {
    size: (instance?.disk_size || instance?.storage_size || 0) * KiloUnit, //GB
    used: instance?.quota_used_total || 0, //MB
    entitledStorage: (instance?.entitled_storage_size || 0) * KiloUnit //GB
  };
  disk.percent =
    disk?.used && disk.size > 0 ? (disk.used * 100) / disk.size : 0;
  Object.keys(resource).forEach((key) => {
    resource[key] = formateDecimal(resource[key]);
  });
  Object.keys(disk).forEach((key) => {
    disk[key] = formateDecimal(disk[key]);
  });
  license = getFAZLicenseWarning({
    disk,
    resource,
    expiry_date: instance?.expiry_date || '9999-01-01',
    days_to_expire: instance?.days_to_expire,
    daysToRemindExpiry,
    evaluateQuotaRatio,
    enable_storage_exceed_warning:
      instance?.enable_storage_exceed_warning || false,
    num_vdom_share_lic: instance?.num_vdom_share_lic,
    entitled_shared_seat: instance?.entitled_shared_seat
  });
  return {
    resource,
    disk,
    license
  };
};
/*************
 * "FAZ:
1. quota exceed:
standalone:
   quota_used_yesterday > entitled_quota_per_day
adom:
   quota_used_last_24_hours > entitled_quota_per_day
2. storage_size exceed:
standalone:
   entitled_storage_size >  disk_size
adom:
   storage_size -- transform --> disk_size

3. License expired:
   expiry_date  <  now  + 30   //warning: expiring
   expiry_date < now    // error: expired

 * "FMG:
1. seat exceed:
standalone & adom:
num_vdom_share_lic > entitled_shared_seat
 *******************/
interface IProps {
  disk: Record<string, any>;
  resource: Record<string, any>;
  days_to_expire: number;
  expiry_date: string;
  daysToRemindExpiry?: number;
  evaluateQuotaRatio?: number;
  enable_storage_exceed_warning: boolean;
  num_vdom_share_lic?: number;
  entitled_shared_seat: number;
}
const getFAZLicenseWarning = ({
  disk,
  resource,
  days_to_expire,
  expiry_date,
  daysToRemindExpiry,
  evaluateQuotaRatio,
  enable_storage_exceed_warning,
  num_vdom_share_lic,
  entitled_shared_seat
}: IProps) => {
  // quota exceeded
  const RATIO = evaluateQuotaRatio || 1;
  const isQuotaExceed = resource.used > resource.size * RATIO;
  const quotaMessage = isQuotaExceed
    ? `yesterday usage: ${resource.used} (MB), entitled quota: ${resource.size} (MB)`
    : '';
  const isStorageExceed =
    enable_storage_exceed_warning && disk.size > disk.entitledStorage * RATIO;
  const storageMessage = isStorageExceed
    ? `entitled storage size: ${
        disk.entitledStorage / KiloUnit
      } (GB), disk size: ${disk.size / KiloUnit} (GB)`
    : '';

  const date1 = DateTime.fromISO(expiry_date);
  const date2 = DateTime.now();
  const dayDiff =
    days_to_expire !== undefined && typeof days_to_expire === 'number'
      ? days_to_expire
      : date1.diff(date2, 'days').toObject().days;
  const isExpired = expiry_date && !(dayDiff && dayDiff > 0);
  const ExpiringFlag = daysToRemindExpiry || 30;
  const isExpiring = !!(
    expiry_date &&
    dayDiff &&
    dayDiff <= ExpiringFlag &&
    dayDiff > 0
  );

  const expiryMessage = isExpired
    ? `${t('common.carouselMessageDialog.expiredMessage')}`
    : isExpiring
    ? `${t('common.carouselMessageDialog.expiringMessage', {
        dayDiff: dayDiff?.toFixed(0)
      })}`
    : '';

  const isSeatExceeded =
    num_vdom_share_lic !== undefined &&
    num_vdom_share_lic > entitled_shared_seat;
  const seatExceedMessage = isSeatExceeded
    ? `Your entitled shared seat is ${entitled_shared_seat}, while the current number of shared licence vdoms is ${num_vdom_share_lic}`
    : '';

  return {
    isQuotaExceed,
    isStorageExceed,
    quotaMessage,
    storageMessage,
    expiryDays: dayDiff,
    isExpiring,
    isExpired,
    isSeatExceeded,
    expiryMessage,
    seatExceedMessage,
    hasAlert:
      isQuotaExceed ||
      isStorageExceed ||
      isExpired ||
      isExpiring ||
      isSeatExceeded,
    hasEntitleAlert: isQuotaExceed || isStorageExceed || isSeatExceeded,
    hasDateAlert: isExpired || isExpiring
  };
};
